import React, { useEffect, useMemo, useRef, useState } from 'react';
import { klona } from 'klona';
import { Icon } from '@fuxi/eevee-icon';
import { ScrollView } from '@/components';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { ShowTitle } from '../show-title';
import {
  setCurrentStep,
  setFormValue,
  setNextBtnConfig,
  setPrevBtnConfig,
  useAIDiagnoseStore,
} from '../../store/useAIDiagnoseStore';
import { DURATION_COLUMN, PainLevelSelector } from '../pain-level-selector';

import cx from './index.module.scss';

interface Props {}

export const PAIN_LEVEL_2_TEXT: Record<AIDiagnose.PainLevel, string> = {
  '1': '轻微',
  '2': '轻度',
  '3': '中度',
  '4': '重度',
  '5': '剧烈',
};

// 肩部
export const MARK_SHOULDER_AREA: AIDiagnose.MarkPainArea[] = ['leftUpperTrapezius', 'rightUpperTrapezius'];

// 背部区域
export const MARK_BACK_AREA: AIDiagnose.MarkPainArea[] = [
  'rightLowerTrapezius',
  'rightInfraspinatus',
  'rightBack',
  'leftBack',
  'leftInfraspinatus',
  'leftLowerTrapezius',
];

// 肩颈区
export const MARK_WAIST_AREA: AIDiagnose.MarkPainArea[] = ['leftWaist', 'rightWaist'];

export const PART_2_TEXT = {
  shoulder: '肩部',
  back: '背部',
  waist: '腰部',
};

export const PainConditionLevel: React.FC<Props> = () => {
  const [isSelectorShow, setIsSelectorShow] = useState(false);
  const { markPainArea, painConditionLevel } = useAIDiagnoseStore.use.formValue();
  const [currentEditField, setCurrentEditField] = useState<keyof typeof PART_2_TEXT>(null);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);

  const selectArea2Part = useMemo(() => {
    return Array.from(
      new Set(
        markPainArea?.map(item =>
          MARK_SHOULDER_AREA.includes(item) ? 'shoulder' : MARK_BACK_AREA.includes(item) ? 'back' : 'waist'
        )
      )
    );
  }, [markPainArea]);

  useEffect(() => {
    setPrevBtnConfig({
      show: true,
      action: () => {
        setCurrentStep(prev => ({ currentStep: prev - 1 }));
      },
    });

    setNextBtnConfig({
      disable: selectArea2Part?.some(item => !painConditionLevel?.[item]?.level),
      action: () => {
        setCurrentStep(prev => ({ currentStep: prev + 1 }));
      },
    });
  }, [painConditionLevel, selectArea2Part]);

  useEffect(() => {
    scrollViewRef?.current?.handleRefresh();
  }, [markPainArea?.length]);

  return (
    <ScrollView ref={scrollViewRef} className={cx('PainConditionLevel-wrapper')}>
      <div className={cx('PainConditionLevel')}>
        <ShowTitle title="您的疼痛情况" />
        <div className={cx('pain-tip')}>
          <div className={cx('pain-tip-item')}>疼痛程度</div>
          <div className={cx('pain-tip-item')}>持续时间</div>
        </div>
        <div className={cx('pain-condition-form')}>
          {klona(selectArea2Part)
            // 表单排序
            ?.sort((a, b) => Object.keys(PART_2_TEXT)?.indexOf(a) - Object.keys(PART_2_TEXT)?.indexOf(b))
            ?.map(item => (
              <div
                className={cx('part-form-item', {
                  'no-value': !painConditionLevel?.[item]?.level,
                })}
                key={item}
                onClick={() => {
                  setCurrentEditField(item);
                  setIsSelectorShow(true);
                }}
              >
                <div className={cx('label')}>{PART_2_TEXT[item]}</div>
                <div className={cx('input-box')}>
                  <div className={cx('input-item')}>
                    <div className={cx('left')}>
                      <span className={cx('value-text')}>
                        {painConditionLevel?.[item]?.level ? (
                          <>
                            <span>{PAIN_LEVEL_2_TEXT[painConditionLevel?.[item]?.level]}</span>
                          </>
                        ) : (
                          <span>请选择</span>
                        )}
                      </span>
                      <span className={cx('value-text')}>
                        {painConditionLevel?.[item]?.duration ? (
                          <span>
                            {DURATION_COLUMN.find(item2 => item2.value === painConditionLevel?.[item]?.duration)?.label}
                          </span>
                        ) : (
                          <span>请选择</span>
                        )}
                      </span>
                    </div>
                    <Icon name="arrow-right" />
                  </div>
                </div>
              </div>
            ))}
        </div>
        <PainLevelSelector
          selectArea2Part={selectArea2Part}
          currentEditField={currentEditField}
          setCurrentEditField={setCurrentEditField}
          isShow={isSelectorShow}
          setIsShow={setIsSelectorShow}
          value={[painConditionLevel?.[currentEditField]?.level, painConditionLevel?.[currentEditField]?.duration]}
          onChange={value =>
            setFormValue(
              {
                painConditionLevel: {
                  [currentEditField]: {
                    level: value?.[0],
                    duration: value?.[1],
                  },
                },
              },
              { isMerge: true }
            )
          }
        />
      </div>
    </ScrollView>
  );
};
